import { createFileRoute } from "@tanstack/react-router";
import {
  EmployeeRoleOptions,
  EmployeeStatusOptions,
} from "@/components/employee/employee-meta";
import { Button } from "@/components/ui/button";
import {
  InputField,
  LightSelectField,
  SelectField,
} from "@/components/ui/form";
import PhoneNumberField from "@/components/ui/form/phone-number-field";
import { useSimpleForm } from "@/hooks/use-simple-form";
import { useTranslate } from "@/hooks/use-translate";
import { cn } from "@/lib/utils";
import useAppStore from "@/store";
import { ChevronLeft } from "lucide-react";
import { useEffect } from "react";

export const Route = createFileRoute("/(dasboard)/employees/$id/edit")({
  component: EmployeePage,
});

function EmployeePage() {
  const store = useAppStore();
  const { t } = useTranslate();
  const employee = store.employee.current;
  const params = Route.useParams();

  const form = useSimpleForm({
    user_id: "",
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "+237",
    role: "seller",
    hire_date: new Date().toISOString().split("T")[0],
    status: "active",
    description: "",
    date_of_birth: "",
    employment_status: "full-time",
    emergency_contact: "",
    salary: "",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });

  useEffect(() => {
    store.employee.fetchOne(params.id).then((employee) => {
      form.setValues({
        user_id: employee.user?.id || "",
        first_name: employee.user?.first_name || "",
        last_name: employee.user?.last_name || "",
        email: employee.user?.email || "",
        phone_number: employee.user?.phone_number || "+237",
        role: employee.role || "seller",
        hire_date: employee.hire_date
          ? employee.hire_date.split("T")[0]
          : undefined,
        status: employee.status || "active",
        description: employee.description || "",
        date_of_birth: employee.date_of_birth
          ? employee.date_of_birth.split("T")[0]
          : undefined,
        employment_status: employee.employment_status || "full-time",
        emergency_contact: employee.emergency_contact || "",
        salary: employee.salary?.toString() || "",
        created_at: employee.created_at || new Date().toISOString(),
        updated_at: employee.updated_at || new Date().toISOString(),
      });
    });
  }, [params.id]);

  const submit = async () => {
    if (!employee) return;

    store.errors.reset();
    store.employee
      .update(employee.id, {
        ...form.values,
        shop_id: store.auth?.session?.shop?.id,
      })
      .then(() => {
        window.location.reload();
      })
      .catch(store.errors.catch);
  };

  const translatedStatusOptions = EmployeeStatusOptions.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const employmentStatusOptions = [
    { value: "full-time", label: t("Full-time") },
    { value: "part-time", label: t("Part-time") },
    { value: "contract", label: t("Contract") },
    { value: "intern", label: t("Intern") },
  ];

  return (
    <section className="max-w-4xl mx-auto">
      <div>
        <button
          type="button"
          onClick={() => window.history.back()}
          className="py-2 inline-flex gap-2 items-center justify-center rounded-lg text-sm text-gray-500 hover:text-gray-900 hover:underline font-semibold"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="">{t("Back")}</span>
        </button>
      </div>
      <div className="mt-4 flex justify-between mb-4">
        <h2 className="text-xl font-semibold">{t("Edit employee")}</h2>
      </div>

      <>{store.errors.render()}</>
      <div className="bg-white p-6 rounded-md border-gray-200">
        <h3 className="font-semibold">{t("Personnal informations")}</h3>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <InputField
            label={t("First Name")}
            name="first_name"
            value={form.values.first_name}
            onChange={form.handleChange}
            error={store.errors.values.first_name}
          />
          <InputField
            label={t("Last Name")}
            name="last_name"
            value={form.values.last_name}
            onChange={form.handleChange}
            error={store.errors.values.last_name}
          />

          <InputField
            label={t("Date of Birth")}
            name="date_of_birth"
            type="date"
            value={form.values.date_of_birth}
            onChange={form.handleChange}
            error={store.errors.values.date_of_birth}
          />
          <div>
            <label
              className={cn("mb-1.5 block text-sm font-medium text-gray-900")}
            >
              {t("Phone number")}
            </label>
            <PhoneNumberField
              value={form.values.phone_number}
              onValueChange={(value: string) => {
                form.setValue("phone_number", value);
              }}
              error={store.errors.values.phone_number}
            />
          </div>
        </div>
      </div>

      <div className="mt-8 bg-white p-6 rounded-md border-gray-200">
        <h3 className="font-semibold"> {t("Employment informations")}</h3>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <LightSelectField
            label={t("Role")}
            name="role"
            value={form.values.role}
            onChange={form.handleChange}
            error={store.errors.values.role}
            options={EmployeeRoleOptions.filter(
              (item: any) => item.value != "shop_owner"
            )}
          />
          <InputField
            label={t("Hire Date")}
            name="hire_date"
            type="date"
            value={form.values.hire_date}
            onChange={form.handleChange}
            error={store.errors.values.hire_date}
          />
          <SelectField
            label={t("Status")}
            name="status"
            value={form.values.status}
            onChange={form.handleChange}
            error={store.errors.values.status}
            options={translatedStatusOptions}
          />
          <SelectField
            label={t("Employment Status")}
            name="employment_status"
            value={form.values.employment_status}
            onChange={form.handleChange}
            error={store.errors.values.employment_status}
            options={employmentStatusOptions}
          />

          <div>
            <label
              className={cn("mb-1.5 block text-sm font-medium text-gray-900")}
            >
              {t("Emergency contact")}
            </label>
            <PhoneNumberField
              value={form.values.emergency_contact}
              onValueChange={(value: string) => {
                form.setValue("emergency_contact", value);
              }}
              error={store.errors.values.emergency_contact}
            />
          </div>

          <InputField
            label={t("Salary")}
            name="salary"
            type="number"
            value={form.values.salary}
            onChange={form.handleChange}
            error={store.errors.values.salary}
          />
        </div>
      </div>

      <Button className="mt-6 px-6 py-2" onClick={submit}>
        {t("Update employee")}
      </Button>
    </section>
  );
}
